import { jsx, jsxs } from "react/jsx-runtime";
import React, { forwardRef, useState, useEffect } from "react";
import { string, bool, number, func } from "prop-types";
import cx from "classnames";
import Button from "@ingka/button";
import plus from "@ingka/ssr-icon/paths/plus";
import minus from "@ingka/ssr-icon/paths/minus";
import minusSmall from "@ingka/ssr-icon/paths/minus-small";
import plusSmall from "@ingka/ssr-icon/paths/plus-small";
import { nullish, increase, decrease, styleWidth, clamp, inputChange, beforeInputChange, clickIncreaseDecrease, onKeyDownEvent, onKeyUpEvent, onBlurEvent, inputValueChange } from "@ingka/quantity-stepper-shared";
const propTypes = {
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Sets the width to 100% */
  fluid: bool,
  /** Makes Quantity Stepper height 40px instead of 56px and makes the icon button extra small */
  small: bool,
  /** Stepper disabled state */
  disabled: bool,
  /** Sets aria-label on the input */
  ariaLabelInput: string,
  /** Sets aria-label on the increase button */
  ariaLabelIncrease: string,
  /** Sets aria-label on the decrease button */
  ariaLabelDecrease: string,
  /** Sets the value for the Stepper and makes it controllable */
  value: number,
  /** Sets the defaultValue for the Stepper and makes it uncontrollable */
  defaultValue: number,
  /** Sets the minimum value for the Stepper */
  minVal: number.isRequired,
  /** Sets the maximum value for the Stepper, can't exceed five digits */
  maxVal: number.isRequired,
  /** Adds description for screen readers on how to interact with the input */
  ariaDescribedBy: string.isRequired,
  /** Sets the id for the aria-describedby element*/
  ariaDescribedById: string.isRequired,
  /** Function called after increase button has been clicked */
  onIncrease: func,
  /** Function called after decrease button has been clicked */
  onDecrease: func,
  /** Function called on input change */
  onInputChange: func,
  /* Function called anytime the value changes */
  onQuantityChange: func,
  /* Function called on the onKeyUp event in the input field */
  onKeyUp: func,
  /* Function called on the onKeyDown event in the input field */
  onKeyDown: func,
  /* Function called on the onBlur event in the input field */
  onBlur: func
};
const QuantityStepper = forwardRef((props, ref) => {
  const {
    prefix = "",
    className,
    fluid,
    small,
    disabled,
    ariaLabelInput,
    ariaLabelIncrease,
    ariaLabelDecrease,
    ariaDescribedBy = "Type in a number or use arrow up or arrow down to change the quantity",
    ariaDescribedById,
    minVal,
    maxVal,
    value,
    defaultValue,
    onIncrease,
    onDecrease,
    onInputChange,
    onQuantityChange,
    onKeyDown,
    onKeyUp,
    onBlur,
    ...other
  } = props;
  const [minValue, setMinValue] = useState(minVal >= 0 ? minVal : 0);
  const [maxValue, setMaxValue] = useState(maxVal);
  const [inputValue, setInputValue] = useState(clamp(defaultValue, minVal, maxVal) || minValue);
  const [ariaValue, setAriaValue] = useState(null);
  const [addClassPlus, setAddClassPlus] = useState(false);
  const [addClassMinus, setAddClassMinus] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [controlled, setControlled] = useState(false);
  const increasedValue = increase(inputValue);
  const decreasedValue = decrease(inputValue);
  const width = maxValue && !fluid ? styleWidth(maxValue.toString()) : null;
  const handleOnFocus = (e) => e.target.select();
  const handleBeforeInputChange = (e) => beforeInputChange(e);
  const handleInputChange = (e) => inputChange(e, setInputValue, minValue, maxValue, onInputChange);
  const handleClickIncrease = () => clickIncreaseDecrease(setInputValue, increasedValue, minValue, maxValue, onIncrease);
  const handleClickDecrease = () => clickIncreaseDecrease(setInputValue, decreasedValue, minValue, maxValue, onDecrease);
  const handleOnKeydown = (e) => onKeyDownEvent(e, inputValue, minValue, maxValue, setInputValue, setReadOnly, setAddClassPlus, setAddClassMinus, increasedValue, decreasedValue, onIncrease, onDecrease, onKeyDown);
  const handleOnKeyUp = (e) => onKeyUpEvent(e, setReadOnly, setAddClassPlus, setAddClassMinus, onKeyUp);
  const handleOnBlur = () => onBlurEvent(inputValue, minValue, setInputValue, onBlur);
  useEffect(() => {
    if (value >= 0)
      setControlled(true);
  }, []);
  useEffect(() => {
    setMinValue(minVal >= 0 ? minVal : 0);
    setMaxValue(maxVal);
  }, [minVal, maxVal]);
  useEffect(() => {
    return inputValueChange(nullish(value, inputValue), setAriaValue, onQuantityChange);
  }, [inputValue, value]);
  const baseClass = `${prefix}quantity-stepper`;
  const classes = cx(
    `${baseClass}`,
    small && `${baseClass}--small`,
    fluid && `${baseClass}--fluid`,
    className
  );
  const inputClass = cx(
    `${baseClass}__input`,
    className
  );
  const increaseClass = cx(
    addClassPlus && `${baseClass}__increase--active`,
    `${baseClass}__increase`
  );
  const decreaseClass = cx(
    addClassMinus && `${baseClass}__decrease--active`,
    `${baseClass}__decrease`
  );
  return /* @__PURE__ */ jsxs("div", { className: classes, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        className: inputClass,
        ref,
        style: width,
        value: nullish(value, inputValue),
        disabled,
        onChange: controlled ? onInputChange : handleInputChange,
        onFocus: handleOnFocus,
        onBlur: controlled ? onBlur : handleOnBlur,
        inputMode: "numeric",
        "aria-label": ariaLabelInput,
        type: "text",
        onKeyDown: controlled ? onKeyDown : handleOnKeydown,
        onKeyUp: controlled ? onKeyUp : handleOnKeyUp,
        min: minValue,
        max: maxValue,
        readOnly,
        "aria-describedby": ariaDescribedById,
        onBeforeInput: handleBeforeInputChange,
        ...other
      }
    ),
    /* @__PURE__ */ jsx("span", { id: ariaDescribedById, className: `${baseClass}__sr-label`, children: ariaDescribedBy }),
    /* @__PURE__ */ jsx(
      Button,
      {
        prefix,
        onClick: controlled ? onIncrease : handleClickIncrease,
        disabled: disabled || nullish(value, inputValue) >= maxValue,
        iconOnly: true,
        small: true,
        xsmallIconOnly: small ? true : null,
        ssrIcon: small ? plusSmall : plus,
        type: "tertiary",
        className: increaseClass,
        "aria-label": ariaLabelIncrease
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        prefix,
        onClick: controlled ? onDecrease : handleClickDecrease,
        disabled: disabled || nullish(value, inputValue) <= minValue,
        iconOnly: true,
        small: true,
        xsmallIconOnly: small ? true : null,
        ssrIcon: small ? minusSmall : minus,
        type: "tertiary",
        className: decreaseClass,
        "aria-label": ariaLabelDecrease
      }
    ),
    /* @__PURE__ */ jsx("span", { className: `${baseClass}__border` }),
    /* @__PURE__ */ jsx("span", { className: `${baseClass}__sr-label`, "aria-live": "assertive", children: ariaValue })
  ] });
});
QuantityStepper.propTypes = propTypes;
QuantityStepper.displayName = "QuantityStepper";
var QuantityStepper_default = QuantityStepper;
export {
  QuantityStepper_default as default
};
